/**
 * Release Manifest 看板（M2 F-2 / AC-F2-1~5）。
 *
 * 對應 BE：
 *   GET /admin/release-manifests              （列表）
 *   GET /admin/release-manifests/:id/assets   （asset 清單 — required / optional）
 *   GET /admin/release-manifests/:id/diff     （per-target diff）
 *
 * AC 重點：
 *   - AC-F2-2：required asset 任一未驗證 → 不切換；看板顯示「失敗 asset」
 *   - AC-F2-3：optional locale 缺漏不阻擋；看板標 missing + fallback 標籤
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  getReleaseManifestAssets,
  getReleaseManifestDiff,
  listReleaseManifests,
  type ReleaseManifestRecord,
  type ReleaseManifestState,
} from '../lib/api-client';

const STATE_STYLE: Record<ReleaseManifestState, { bg: string; color: string; label: string }> = {
  staging: { bg: '#fef3c7', color: '#92400e', label: 'staging' },
  active: { bg: '#dcfce7', color: '#065f46', label: 'active' },
  archived: { bg: '#e5e7eb', color: '#374151', label: 'archived' },
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

function StateBadge({ state }: { state: ReleaseManifestState }): JSX.Element {
  const s = STATE_STYLE[state] ?? { bg: '#e5e7eb', color: '#374151', label: state };
  return (
    <span className="badge" style={{ background: s.bg, color: s.color, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

/** 選定 manifest 後之詳情：asset 清單 + per-target diff。 */
function ManifestDetail({ releaseId }: { releaseId: string }): JSX.Element {
  const [targetIdInput, setTargetIdInput] = useState('');
  const [diffTargetId, setDiffTargetId] = useState<string | null>(null);

  const assetsQuery = useQuery({
    queryKey: ['release-manifest.assets', releaseId],
    queryFn: () => getReleaseManifestAssets(releaseId),
  });
  const diffQuery = useQuery({
    queryKey: ['release-manifest.diff', releaseId, diffTargetId],
    queryFn: () => getReleaseManifestDiff(releaseId, diffTargetId as string),
    enabled: diffTargetId !== null,
  });

  const assets = assetsQuery.data?.items ?? [];
  const requiredAssets = assets.filter((a) => a.required);
  const optionalAssets = assets.filter((a) => !a.required);

  return (
    <section
      style={{
        marginTop: 16,
        padding: 16,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 4,
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 16 }}>
        {releaseId} — Asset 清單
        <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>
          required {requiredAssets.length} / optional {optionalAssets.length}
        </span>
      </h2>

      {assetsQuery.isLoading && <div>載入中…</div>}
      {assetsQuery.error && (
        <div className="error-banner">{(assetsQuery.error as Error).message}</div>
      )}
      {assetsQuery.data && (
        <table>
          <thead>
            <tr>
              <th>asset_id</th>
              <th>locale</th>
              <th>media_type</th>
              <th>必要性</th>
              <th>sha256</th>
            </tr>
          </thead>
          <tbody>
            {assets.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>
                  此 manifest 無 asset
                </td>
              </tr>
            )}
            {assets.map((a) => (
              <tr key={`${a.assetId}:${a.locale}`}>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{a.assetId}</td>
                <td>{a.locale}</td>
                <td style={{ fontSize: 12 }}>{a.mediaType}</td>
                <td>
                  {a.required ? (
                    <span className="badge danger">required</span>
                  ) : (
                    <span className="badge">optional</span>
                  )}
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>
                  {a.sha256.slice(0, 12)}…
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* per-target diff */}
      <h3 style={{ fontSize: 14, marginBottom: 4 }}>Per-target Diff（AC-F2-5）</h3>
      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 0 }}>
        輸入 sync target ID 比對該目標已同步狀態；missing required asset 任一未過即阻擋切換
        （AC-F2-2），optional 缺漏僅標示、不阻擋（AC-F2-3）。
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          type="text"
          value={targetIdInput}
          onChange={(e) => setTargetIdInput(e.target.value)}
          placeholder="sync target ID"
          style={{ fontFamily: 'monospace', padding: 6, minWidth: 320 }}
        />
        <button
          onClick={() => setDiffTargetId(targetIdInput.trim() || null)}
          disabled={targetIdInput.trim().length === 0}
        >
          比對
        </button>
      </div>

      {diffQuery.isLoading && <div>比對中…</div>}
      {diffQuery.error && <div className="error-banner">{(diffQuery.error as Error).message}</div>}
      {diffQuery.data && (
        <div>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            目標現行版本：
            <strong style={{ fontFamily: 'monospace' }}>
              {diffQuery.data.targetCurrentVersion ?? '（無）'}
            </strong>
          </div>
          {diffQuery.data.missingAssets.length === 0 ? (
            <div className="badge success" style={{ display: 'inline-block', padding: '4px 10px' }}>
              ✅ required asset 全數就緒 — 可切換
            </div>
          ) : (
            <div
              style={{
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: 4,
                padding: 10,
              }}
            >
              <div style={{ fontWeight: 700, color: '#b91c1c', marginBottom: 4 }}>
                ⛔ {diffQuery.data.missingAssets.length} 個 required asset 未驗證 —
                阻擋切換（AC-F2-2）
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, fontFamily: 'monospace' }}>
                {diffQuery.data.missingAssets.map((m) => (
                  <li key={`${m.asset_id}:${m.locale}`}>
                    {m.asset_id} ({m.locale})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {diffQuery.data.missingLocales.length > 0 && (
            <div
              style={{
                marginTop: 8,
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: 4,
                padding: 10,
                fontSize: 12,
              }}
            >
              <strong style={{ color: '#92400e' }}>缺漏 locale（不阻擋；前端走 fallback）：</strong>{' '}
              {diffQuery.data.missingLocales.join(', ')}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function ReleaseManifestBoardPage(): JSX.Element {
  const [selected, setSelected] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['release-manifests.list'],
    queryFn: () => listReleaseManifests(100),
  });

  const items: ReleaseManifestRecord[] = listQuery.data?.items ?? [];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">Release Manifest 看板（F-2 / AC-F2-1~5）</h1>
        <div className="page-head__actions">
          <button onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
            {listQuery.isFetching ? '更新中…' : '重新整理'}
          </button>
        </div>
      </div>

      <p style={{ color: '#6b7280' }}>
        對外發布之 Release Manifest（簽章 + per-target 同步狀態）；點選一筆查看 asset 清單與
        per-target diff。
      </p>

      {listQuery.isLoading && <div>載入中…</div>}
      {listQuery.error && <div className="error-banner">{(listQuery.error as Error).message}</div>}
      {listQuery.data && (
        <table>
          <thead>
            <tr>
              <th>release_id</th>
              <th>內容版本</th>
              <th>目標</th>
              <th>狀態</th>
              <th>必要 locale</th>
              <th>建立時間</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>
                  尚無 release manifest
                </td>
              </tr>
            )}
            {items.map((m) => (
              <tr
                key={m.id}
                onClick={() => setSelected(m.release_id)}
                style={{
                  cursor: 'pointer',
                  background: selected === m.release_id ? '#eff6ff' : undefined,
                }}
              >
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{m.release_id}</td>
                <td>{m.content_version}</td>
                <td>
                  <span className="badge">{m.target_type}</span>
                </td>
                <td>
                  <StateBadge state={m.state} />
                </td>
                <td style={{ fontSize: 12 }}>{m.required_locales.join(', ')}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{formatDateTime(m.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && <ManifestDetail releaseId={selected} />}
    </div>
  );
}
